<script setup lang="ts">
  import { formatDate } from '~/helpers/formatDate'
  import type { INotification } from '~/types/notifications'

  const emit = defineEmits<{
    click: [notification: INotification]
  }>()

  const props = withDefaults(
    defineProps<{
      notification: INotification
      compact?: boolean
    }>(),
    { compact: false }
  )

  const { t } = useI18n()

  const categoryLabel = computed(() => {
    const category = props.notification?.category
    return category ? t(`notifications.categories.${category}`) : ''
  })

  const formattedDate = computed(() =>
    formatDate(props.notification?.createdAt, { withTime: true })
  )

  const severityIcon = computed(() => {
    if (props.notification?.severity === 'critical') return 'i-lucide-circle-alert'
    if (props.notification?.severity === 'warning') return 'i-lucide-triangle-alert'
    return 'i-lucide-info'
  })

  const severityIconClass = computed(() => {
    if (props.notification?.severity === 'critical') return 'bg-error/10 text-error'
    if (props.notification?.severity === 'warning') return 'bg-warning/10 text-warning'
    return 'bg-primary/10 text-primary'
  })
</script>

<template>
  <button
    type="button"
    class="group flex w-full items-start gap-3 rounded-xl border border-transparent px-3 py-3 text-left transition-colors"
    :class="[
      notification.isRead ? 'bg-transparent hover:bg-elevated/60' : 'bg-primary/5 hover:bg-primary/10',
      !notification.actionUrl ? 'cursor-default' : 'cursor-pointer',
    ]"
    @click="emit('click', notification)"
  >
    <span
      class="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full"
      :class="severityIconClass"
    >
      <UIcon :name="severityIcon" class="size-5" />
    </span>

    <span class="min-w-0 flex-1">
      <span class="flex items-start gap-2">
        <span class="line-clamp-2 min-w-0 flex-1 text-sm font-medium text-highlighted">
          {{ notification.title }}
        </span>

        <span
          v-if="!notification.isRead"
          class="mt-1 size-2 shrink-0 rounded-full bg-primary"
        />
      </span>

      <span v-if="!compact && notification.body" class="mt-1 line-clamp-2 block text-sm text-muted">
        {{ notification.body }}
      </span>

      <span class="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted">
        <span v-if="categoryLabel">{{ categoryLabel }}</span>

        <span v-if="categoryLabel">·</span>

        <span>{{ formattedDate }}</span>
      </span>
    </span>
  </button>
</template>
